import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router";
import FunctionsIcon from "@mui/icons-material/Functions";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutlined";
import { Button } from "@/components/common/Button";
import { SearchBar } from "@/components/common/SearchBar";
import { SectionRule } from "components/common/SectionRule";
import { ListEmptyShell } from "@/components/main/overview/ListEmptyShell";
import { RecipeCatalogRow } from "@/components/main/recipes/RecipeCatalogRow";
import { RecipeCatalogDialog } from "@/components/main/recipes/RecipeCatalogDialog";
import { useOrgContextStore } from "@/components/main/stores/orgContextStore";
import { VersionChip } from "@/components/main/utils/common/VersionChip";
import { showNotificationGlobal } from "@/stores/useNotificationStore";
import {
  listRecipes,
  describeRecipeError,
  type RecipeListItem,
} from "@/components/canvas/recipes/recipesApi";
import { ColumnTopSpacer, ListBox } from "@/components/main/pages/pageColumn";

/// Typing filters server-side (trigram over the whole org), so the keystroke
/// has to settle before it costs a query.
const SEARCH_DEBOUNCE_MS = 250;

/// The org catalog of recipes: everything published in the active org plus the
/// caller's own, both narrowed by R7 to recipes whose every input table they can
/// read. One org, never a union — the page has an org picker on screen, so an
/// answer spanning orgs would not match what it says.
///
/// The list pages by keyset; typing swaps it for a relevance-ranked search of
/// the same org, which does not page.
export const RecipesPage: React.FC = () => {
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);
  const orgsLoaded = useOrgContextStore((s) => s.orgsLoaded);
  const [recipes, setRecipes] = useState<RecipeListItem[]>([]);
  const [next, setNext] = useState<{ name: string; recipe_id: string } | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filter, setFilter] = useState("");
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState<RecipeListItem | null>(null);
  const [reloads, setReloads] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setQuery(filter.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [filter]);

  useEffect(() => {
    // The door requires an org, so there is nothing to ask for until one is
    // resolved — but once the orgs are in and there is still none, the spinner
    // has to stop rather than run forever.
    if (!activeOrgId) {
      if (orgsLoaded) setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    listRecipes({ orgId: activeOrgId, q: query || undefined })
      .then((page) => {
        if (cancelled) return;
        setRecipes(page.recipes);
        setNext(page.next ?? null);
      })
      .catch((err) => {
        if (!cancelled)
          showNotificationGlobal(describeRecipeError(err), "error");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [activeOrgId, orgsLoaded, query, reloads]);

  const loadMore = useCallback(async () => {
    if (!activeOrgId || !next || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await listRecipes({ orgId: activeOrgId, after: next });
      setRecipes((prev) => [...prev, ...page.recipes]);
      setNext(page.next ?? null);
    } catch (err) {
      showNotificationGlobal(describeRecipeError(err), "error");
    } finally {
      setLoadingMore(false);
    }
  }, [activeOrgId, next, loadingMore]);

  // Deprecated last within each group: still listed — its results point at it —
  // but never the first thing anyone reads.
  const [mine, published] = useMemo(() => {
    const byShelf = (a: RecipeListItem, b: RecipeListItem) =>
      Number(a.deprecated) - Number(b.deprecated);
    return [
      recipes.filter((r) => r.mine).sort(byShelf),
      recipes.filter((r) => !r.mine).sort(byShelf),
    ];
  }, [recipes]);

  // The list scrolls inside a capped column, so "the end" is this container's,
  // not the window's.
  const listRef = useRef<HTMLDivElement | null>(null);
  const onScroll = () => {
    const el = listRef.current;
    if (!el || !next || loadingMore) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 40) void loadMore();
  };

  const reload = () => setReloads((n) => n + 1);

  const isEmpty = recipes.length === 0 && !query;

  return (
    <div className="relative flex h-full w-full flex-col items-center overflow-y-auto px-6">
      <ColumnTopSpacer />
      <div
        className="flex w-full flex-col"
        style={{
          maxWidth: "45rem",
          paddingBottom: "2.5rem",
        }}
      >
        {isEmpty ? (
          <ListEmptyShell
            icon={<FunctionsIcon sx={{ fontSize: 14 }} />}
            label="Recipes"
            hint={
              <>
                <Link to="/access" className="text-accent hover:underline">
                  Connect via MCP
                </Link>{" "}
                and let your agent write an analysis.
              </>
            }
            countLabel="0 recipes"
            loading={loading}
            search={{
              value: filter,
              onChange: setFilter,
              placeholder: "Search recipes…",
            }}
          />
        ) : (
          <>
            <div className="mb-6 shrink-0">
              <SearchBar
                value={filter}
                onChange={setFilter}
                placeholder="Search recipes…"
                size="lg"
                isLoading={loading}
              />
            </div>

            <ListBox ref={listRef} onScroll={onScroll}>
              <div className="list-scope-enter">
                {mine.length > 0 && (
                  <div className="mb-8">
                    <div className="sticky top-0 z-20 overflow-hidden bg-bg">
                      <SectionRule
                        inset
                        icon={<FunctionsIcon sx={{ fontSize: 14 }} />}
                        label="Yours"
                      />
                    </div>
                    {mine.map((recipe) => (
                      <RecipeCatalogRow
                        key={recipe.recipe_id}
                        recipe={recipe}
                        onOpen={() => setOpen(recipe)}
                      />
                    ))}
                  </div>
                )}

                {published.length > 0 && (
                  <div className="mb-8">
                    <div className="sticky top-0 z-20 overflow-hidden bg-bg">
                      <SectionRule
                        inset
                        icon={<PeopleOutlineIcon sx={{ fontSize: 14 }} />}
                        label="Published in this org"
                      />
                    </div>
                    {published.map((recipe) => (
                      <RecipeCatalogRow
                        key={recipe.recipe_id}
                        recipe={recipe}
                        onOpen={() => setOpen(recipe)}
                      />
                    ))}
                  </div>
                )}

                {recipes.length === 0 && (
                  <div className="py-12 text-center text-small italic text-fg-muted">
                    {`No recipes match “${query}”.`}
                  </div>
                )}
              </div>
            </ListBox>

            <div className="mt-6 flex shrink-0 items-center">
              <span
                className="font-mono text-fg-muted"
                style={{
                  fontSize: "var(--font-size-x-small)",
                  letterSpacing: "0.04em",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {[
                  `${mine.length} yours`,
                  ...(published.length > 0
                    ? [`${published.length} published`]
                    : []),
                ].join(" · ")}
              </span>
              {next && (
                <span className="ml-auto">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={loadMore}
                    loading={loadingMore}
                  >
                    Load more
                  </Button>
                </span>
              )}
            </div>
          </>
        )}
      </div>
      <div className="absolute bottom-4 right-4 z-10">
        <VersionChip />
      </div>

      {open && (
        <RecipeCatalogDialog
          recipe={open}
          onClose={() => setOpen(null)}
          onChanged={reload}
        />
      )}
    </div>
  );
};
